import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { CartContext } from '../context/CartContext';
import '../styles/main.css';
import GoBackButton from './GoBackButton';

export default function Cart() {
  const { cartItems, updateQuantity, removeFromCart } = useContext(CartContext);
  const navigate = useNavigate();
  const { businessName, salePointName } = useParams();
  const [validSalePoint, setValidSalePoint] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!businessName || !salePointName) {
      setValidSalePoint(false);
      return;
    }

    const validateSalePoint = async () => {
      try {
        const res = await fetch(`/api/salePointConfig/${businessName}/${salePointName}`);
        if (res.status === 404) {
          setValidSalePoint(false);
          return;
        }
        if (!res.ok) throw new Error('Failed to fetch config');
        const config = await res.json();

        if (config.salePointBannerUrl) {
          setValidSalePoint(true);
        } else {
          setValidSalePoint(false);
        }
      } catch (err) {
        console.warn('Validation error:', err.message);
        setValidSalePoint(false);
      }
    };

    validateSalePoint();
  }, [businessName, salePointName]);

  useEffect(() => {
    if (validSalePoint === false) {
      navigate('/not-found');
    }
  }, [validSalePoint, navigate]);

  useEffect(() => {
    if (cartItems.length > 0 && validSalePoint === true) {
      setIsVisible(true);
    }
  }, [cartItems, validSalePoint]);

  if (validSalePoint === null) return <div className="cart-summary"><p>جارٍ التحقق من نقطة البيع...</p></div>;

  if (cartItems.length === 0) return <div className="cart-summary empty-cart"><p>عربة التسوق الخاصة بك فارغة</p></div>;

  const total = cartItems.reduce((sum, i) => {
    const qty = typeof i.quantity === 'number' && !isNaN(i.quantity) ? i.quantity : 1;
    return sum + i.price * qty;
  }, 0);

  const getImageUrl = (filename) => {
    return filename;
  };

  return (
    <div className={`cart-container ${isVisible ? 'cart-visible' : ''}`} style={{ position: 'relative' }}>
      <div style={{ 
        position: 'sticky', 
        top: '80px', 
        left: '10px', 
        zIndex: 1000,
        alignSelf: 'flex-start'
      }}>
        <GoBackButton />
      </div>
      
      <div className="cart-header">
        <h2>عربة التسوق</h2>
        <span className="cart-count-badge">{cartItems.length}</span>
      </div>
      
      <ul className="cart-list">
        {cartItems.map((item, index) => (
          <li key={item.cartLineId} className="cart-item" style={{ '--item-index': index }}>
            <div className="cart-card">
              <div className="cart-image-wrapper">
                <img
                  src={getImageUrl(item.imageUrl)}
                  alt={item.name}
                  className="cart-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/assets/SalePoint1631330663733.jpg';
                  }}
                />
              </div>
              
              <div className="cart-content">
                <h4 className="cart-name">{item.name}</h4>
                {item.combinationText && (
                  <p className="cart-variant-text">{item.combinationText}</p>
                )}
                <p className="cart-price">{item.price.toFixed(2)} دينار عراقي</p>
                
                 <div className="cart-controls">
                   <button
                     className="qty-btn"
                     onClick={() => updateQuantity(item.cartLineId, Math.max(1, item.quantity - 1))}
                   >
                     −
                   </button>
                   <span className="qty-display">{typeof item.quantity === 'number' && !isNaN(item.quantity) ? item.quantity : 1}</span>
                   <button
                     className="qty-btn"
                     disabled={
                       // Disable plus if: item is restricted AND we have stock info AND quantity >= stock
                       item.sellIfNotProvidedInStore === false &&
                       typeof item.stockQuantity === 'number' &&
                       item.quantity >= item.stockQuantity
                     }
                     onClick={() => updateQuantity(item.cartLineId, item.quantity + 1)}
                     title={
                       item.sellIfNotProvidedInStore === false &&
                       typeof item.stockQuantity === 'number' &&
                       item.quantity >= item.stockQuantity
                         ? 'تم إضافة الكمية المتاحة'
                         : 'زيادة الكمية'
                     }
                   >
                     +
                   </button>
                 </div>

                 {item.sellIfNotProvidedInStore === false && typeof item.stockQuantity === 'number' && (
                   <p className="stock-info">الكمية المتاحة: <strong>{item.stockQuantity}</strong></p>
                 )}

                <p className="item-total">المجموع: <strong className='item-tot-price-number'>{(item.price * (typeof item.quantity === 'number' && !isNaN(item.quantity) ? item.quantity : 1)).toFixed(2)} دينار عراقي</strong></p>
              </div>

              <button 
                className="btn-remove" 
                onClick={() => removeFromCart(item.cartLineId)}
                title="حذف المنتج"
              >
                <FaTrash className='btn-icon'/>
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="cart-summary">
        <div className="cart-total-section">
          <p className="total-label">المجموع الكلي:</p>
          <p className="total-amount">{total.toFixed(2)} دينار عراقي</p>
        </div>
        <button
          className="btn-checkout"
          onClick={() =>
            navigate(`/${businessName}/${salePointName}/checkout`, {
              state: {
                items: cartItems.map(item => ({
                  item_id: item.id,
                  quantity: item.quantity,
                  combinationId: item.combinationId ?? null
                }))
              }
            })
          }
        >
          إستكمال عملية الشراء
        </button>
      </div>
    </div>
  );
}